import React, { useState, useEffect, useMemo } from "react";
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Instagram,
  Plus,
  Minus,
  Info,
} from "lucide-react";
import {
  subscribeToCategories,
  subscribeToPackages,
  subscribeToAddOns,
  createBooking,
} from "../services/firestore";
import { formatCurrency } from "../types/booking";
import SEO from "./SEO";

const STEPS = [
  { id: 1, name: "Pilih Paket", icon: "📦" },
  { id: 2, name: "Tanggal & Waktu", icon: "📅" },
  { id: 3, name: "Detail Klien", icon: "👤" },
  { id: 4, name: "Tambahan", icon: "➕" },
  { id: 5, name: "Konfirmasi", icon: "✅" },
];

const BookingForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  const [formData, setFormData] = useState({
    // Step 1: Package selection (handled in selectedPackages)
    // Step 2: Date & Time
    eventDate: "",
    eventTime: "",
    eventLocation: "",
    // Step 3: Client Details
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientInstagram: "",
    specialRequests: "",
    // Step 4: Add-ons (handled in selectedAddOns)
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Load data
  useEffect(() => {
    const unsubscribeCategories = subscribeToCategories((data) => {
      const activeCategories = data.filter((c) => c.isActive !== false);
      setCategories(activeCategories);
      if (activeCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(activeCategories[0].id);
      }
    });

    const unsubscribePackages = subscribeToPackages((data) => {
      const activePackages = data.filter((p) => p.isActive !== false);
      setPackages(activePackages);
      setLoading(false);
    });

    const unsubscribeAddOns = subscribeToAddOns((data) => {
      setAddOns(data.filter((a) => a.isActive !== false));
    });

    return () => {
      unsubscribeCategories();
      unsubscribePackages();
      unsubscribeAddOns();
    };
  }, []);

  // Filter packages by category
  const filteredPackages = useMemo(() => {
    if (!selectedCategory) return packages;
    return packages.filter((pkg) => pkg.categoryId === selectedCategory);
  }, [packages, selectedCategory]);

  // Get category name
  const getCategoryName = useMemo(() => {
    const categoryMap = new Map(categories.map((c) => [c.id, c.name]));
    return (categoryId) => categoryMap.get(categoryId) || "";
  }, [categories]);

  // Filter add-ons by selected packages' availableAddOns
  const filteredAddOns = useMemo(() => {
    const allowedIds = new Set(
      selectedPackages.flatMap(pkg => pkg.availableAddOns || [])
    );
    if (allowedIds.size === 0) return [];
    return addOns.filter(addon => allowedIds.has(addon.id));
  }, [addOns, selectedPackages]);

  // Calculate totals
  const totals = useMemo(() => {
    const packagesTotal = selectedPackages.reduce(
      (sum, pkg) => sum + (pkg.price || 0),
      0,
    );
    const addOnsTotal = selectedAddOns.reduce((sum, addOn) => {
      const quantity = addOn.quantity || 1;
      return sum + (addOn.price || 0) * quantity;
    }, 0);
    const subtotal = packagesTotal + addOnsTotal;
    const depositAmount = Math.round(subtotal * 0.3); // 30% deposit
    const remainingAmount = subtotal - depositAmount;

    return {
      packagesTotal,
      addOnsTotal,
      subtotal,
      depositPercent: 30,
      depositAmount,
      remainingAmount,
    };
  }, [selectedPackages, selectedAddOns]);

  // Get available time slots
  const timeSlots = [
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
  ];

  // Package selection handlers
  const togglePackage = (pkg) => {
    setSelectedPackages((prev) => {
      const exists = prev.find((p) => p.id === pkg.id);
      if (exists) {
        return prev.filter((p) => p.id !== pkg.id);
      } else {
        return [...prev, pkg];
      }
    });
    setTouched((prev) => ({ ...prev, packages: true }));
    setErrors((prev) => ({ ...prev, packages: "" }));
  };

  const isPackageSelected = (pkgId) => {
    return selectedPackages.some((p) => p.id === pkgId);
  };

  // Add-on selection handlers
  const toggleAddOn = (addOn) => {
    setSelectedAddOns((prev) => {
      const exists = prev.find((a) => a.id === addOn.id);
      if (exists) {
        return prev.filter((a) => a.id !== addOn.id);
      } else {
        return [...prev, { ...addOn, quantity: 1 }];
      }
    });
  };

  const updateAddOnQuantity = (addOnId, quantity) => {
    if (quantity < 1) return;
    setSelectedAddOns((prev) =>
      prev.map((addOn) =>
        addOn.id === addOnId ? { ...addOn, quantity } : addOn,
      ),
    );
  };

  const isAddOnSelected = (addOnId) => {
    return selectedAddOns.some((a) => a.id === addOnId);
  };

  const getAddOnQuantity = (addOnId) => {
    const addOn = selectedAddOns.find((a) => a.id === addOnId);
    return addOn?.quantity || 0;
  };

  // Form input handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Validation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (selectedPackages.length === 0) {
        newErrors.packages = "Silakan pilih minimal 1 paket";
      }
    }

    if (step === 2) {
      if (!formData.eventDate) {
        newErrors.eventDate = "Tanggal acara wajib diisi";
      } else {
        const selectedDate = new Date(formData.eventDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          newErrors.eventDate = "Tanggal acara tidak boleh di masa lalu";
        }
      }
      if (!formData.eventTime) {
        newErrors.eventTime = "Jam mulai wajib diisi";
      }
      if (!formData.eventLocation.trim()) {
        newErrors.eventLocation = "Lokasi acara wajib diisi";
      }
    }

    if (step === 3) {
      if (!formData.clientName.trim()) {
        newErrors.clientName = "Nama wajib diisi";
      }
      if (!formData.clientEmail.trim()) {
        newErrors.clientEmail = "Email wajib diisi";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
        newErrors.clientEmail = "Email tidak valid";
      }
      if (!formData.clientPhone.trim()) {
        newErrors.clientPhone = "No. WhatsApp wajib diisi";
      } else if (
        !/^08\d{8,11}$/.test(formData.clientPhone.replace(/[-\s]/g, ""))
      ) {
        newErrors.clientPhone =
          "Nomor WhatsApp tidak valid (harus dimulai dengan 08)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToStep = (step) => {
    if (step < currentStep || validateStep(currentStep)) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Submit booking
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        eventDate: new Date(formData.eventDate),
        items: [
          ...selectedPackages.map((pkg) => ({
            type: "PACKAGE",
            packageId: pkg.id,
            packageSnapshot: pkg,
            price: pkg.price,
            quantity: 1,
            subtotal: pkg.price,
          })),
          ...selectedAddOns.map((addOn) => ({
            type: "ADDON",
            addOnId: addOn.id,
            addOnSnapshot: addOn,
            price: addOn.price,
            quantity: addOn.quantity || 1,
            subtotal: (addOn.price || 0) * (addOn.quantity || 1),
          })),
        ],
        depositPercent: totals.depositPercent,
        depositAmount: totals.depositAmount,
        totalAmount: totals.subtotal,
        paidAmount: 0,
        remainingAmount: totals.remainingAmount,
        source: "website",
        status: "PENDING_DEPOSIT",
      };

      const result = await createBooking(bookingData);

      // Redirect to confirmation page
      window.location.href = `/booking/confirmation?ref=${result.id}`;
    } catch (error) {
      console.error("Error creating booking:", error);
      alert(error.message || "Gagal membuat booking. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF7A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#F7F5F2]">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Booking Henna | Pesan Layanan Henna Profesional"
        description="Booking layanan henna profesional untuk wedding, engagement, tunangan, dan acara spesial Anda. Pilih paket dan jadwal sesuai kebutuhan."
        keywords="booking henna, pesan henna, jadwal henna, booking henna wedding, harga henna"
        url="https://tehputrihenna.com/booking"
      />
      <div className="min-h-screen bg-gradient-to-b from-black to-[#0A0A0A] py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-[#F7F5F2] mb-4">
              Booking Henna Artist
            </h1>
            <p className="text-lg text-[#F7F5F2]/70">
              Isi form di bawah untuk memesan jasa henna untuk acara spesial
              Anda
            </p>
          </div>

          {/* Step Indicator */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex items-center justify-between min-w-max">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => goToStep(step.id)}
                    className={`flex flex-col items-center gap-2 transition-all ${
                      step.id <= currentStep ? "opacity-100" : "opacity-50"
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                        step.id === currentStep
                          ? "bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black scale-110"
                          : step.id < currentStep
                            ? "bg-green-500 text-white"
                            : "bg-white/10 text-[#F7F5F2]"
                      }`}
                    >
                      {step.id < currentStep ? "✓" : step.icon}
                    </div>
                    <span
                      className={`text-xs font-medium whitespace-nowrap ${
                        step.id === currentStep
                          ? "text-[#D4AF7A]"
                          : "text-[#F7F5F2]/70"
                      }`}
                    >
                      {step.name}
                    </span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-4 ${
                        step.id < currentStep ? "bg-green-500" : "bg-white/10"
                      }`}
                      style={{ maxWidth: "60px" }}
                    ></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-6 sm:p-8">
            {/* Step 1: Package Selection */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F7F5F2] mb-2">
                    Pilih Paket Henna
                  </h2>
                  <p className="text-[#F7F5F2]/70">
                    Pilih minimal 1 paket untuk melanjutkan
                  </p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full font-medium transition-all ${
                        selectedCategory === category.id
                          ? "bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black"
                          : "bg-white/5 text-[#F7F5F2] border border-[#D4AF7A]/30 hover:bg-white/10"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>

                {errors.packages && touched.packages && (
                  <div className="p-3 border rounded-lg bg-red-500/10 border-red-500/30">
                    <p className="text-sm text-red-400">{errors.packages}</p>
                  </div>
                )}

                {/* Package Cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {filteredPackages.map((pkg) => {
                    const isSelected = isPackageSelected(pkg.id);
                    return (
                      <div
                        key={pkg.id}
                        onClick={() => togglePackage(pkg)}
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "border-[#D4AF7A] bg-[#D4AF7A]/10"
                            : "border-[#D4AF7A]/20 bg-white/5 hover:border-[#D4AF7A]/40"
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-6 h-6 bg-[#D4AF7A] rounded-full flex items-center justify-center">
                            <Check size={14} className="text-black" />
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-[#F7F5F2] mb-1">
                              {pkg.name}
                            </h3>
                            {pkg.subcategoryName && (
                              <span className="text-xs text-[#D4AF7A]">
                                {pkg.subcategoryName}
                              </span>
                            )}
                            <p className="text-2xl font-bold text-[#D4AF7A] mt-2">
                              {formatCurrency(pkg.price)}
                            </p>
                            {pkg.description && (
                              <p className="text-sm text-[#F7F5F2]/60 mt-2 line-clamp-2">
                                {pkg.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected Packages Summary */}
                {selectedPackages.length > 0 && (
                  <div className="p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[#F7F5F2]">
                        {selectedPackages.length} Paket Dipilih
                      </span>
                      <span className="text-lg font-bold text-[#D4AF7A]">
                        {formatCurrency(totals.packagesTotal)}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {selectedPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className="flex items-center gap-2 text-sm text-[#F7F5F2]/80"
                        >
                          <Check size={14} className="text-[#D4AF7A]" />
                          {pkg.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Date & Time */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F7F5F2] mb-2">
                    Tanggal & Waktu
                  </h2>
                  <p className="text-[#F7F5F2]/70">Informasi acara Anda</p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Event Date */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Tanggal Acara *
                    </label>
                    <input
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) =>
                        handleInputChange("eventDate", e.target.value)
                      }
                      min={new Date().toISOString().split("T")[0]}
                      className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                        errors.eventDate && touched.eventDate
                          ? "border-red-500"
                          : "border-[#D4AF7A]/30"
                      }`}
                    />
                    {errors.eventDate && touched.eventDate && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.eventDate}
                      </p>
                    )}
                  </div>

                  {/* Event Time */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Jam Mulai *
                    </label>
                    <select
                      value={formData.eventTime}
                      onChange={(e) =>
                        handleInputChange("eventTime", e.target.value)
                      }
                      className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                        errors.eventTime && touched.eventTime
                          ? "border-red-500"
                          : "border-[#D4AF7A]/30"
                      }`}
                    >
                      <option value="">Pilih jam</option>
                      {timeSlots.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                    {errors.eventTime && touched.eventTime && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.eventTime}
                      </p>
                    )}
                  </div>
                </div>

                {/* Event Location */}
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Lokasi Acara *
                  </label>
                  <input
                    type="text"
                    value={formData.eventLocation}
                    onChange={(e) =>
                      handleInputChange("eventLocation", e.target.value)
                    }
                    placeholder="Contoh: Hotel Mulia Senayan, Jakarta"
                    className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                      errors.eventLocation && touched.eventLocation
                        ? "border-red-500"
                        : "border-[#D4AF7A]/30"
                    }`}
                  />
                  {errors.eventLocation && touched.eventLocation && (
                    <p className="mt-1 text-sm text-red-400">
                      {errors.eventLocation}
                    </p>
                  )}
                </div>

              </div>
            )}

            {/* Step 3: Client Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F7F5F2] mb-2">
                    Informasi Kontak
                  </h2>
                  <p className="text-[#F7F5F2]/70">
                    Data diri Anda untuk kami hubungi
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Nama Lengkap *
                    </label>
                    <input
                      type="text"
                      value={formData.clientName}
                      onChange={(e) =>
                        handleInputChange("clientName", e.target.value)
                      }
                      placeholder="Nama Anda"
                      className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                        errors.clientName && touched.clientName
                          ? "border-red-500"
                          : "border-[#D4AF7A]/30"
                      }`}
                    />
                    {errors.clientName && touched.clientName && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.clientName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.clientEmail}
                      onChange={(e) =>
                        handleInputChange("clientEmail", e.target.value)
                      }
                      placeholder="email@example.com"
                      className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                        errors.clientEmail && touched.clientEmail
                          ? "border-red-500"
                          : "border-[#D4AF7A]/30"
                      }`}
                    />
                    {errors.clientEmail && touched.clientEmail && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.clientEmail}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      No. WhatsApp *
                    </label>
                    <input
                      type="tel"
                      value={formData.clientPhone}
                      onChange={(e) =>
                        handleInputChange("clientPhone", e.target.value)
                      }
                      placeholder="08xxxxxxxxxx"
                      className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                        errors.clientPhone && touched.clientPhone
                          ? "border-red-500"
                          : "border-[#D4AF7A]/30"
                      }`}
                    />
                    {errors.clientPhone && touched.clientPhone && (
                      <p className="mt-1 text-sm text-red-400">
                        {errors.clientPhone}
                      </p>
                    )}
                  </div>

                  {/* Instagram */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                      Instagram (Opsional)
                    </label>
                    <input
                      type="text"
                      value={formData.clientInstagram}
                      onChange={(e) =>
                        handleInputChange("clientInstagram", e.target.value)
                      }
                      placeholder="@username"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                    />
                  </div>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Request Khusus (Opsional)
                  </label>
                  <textarea
                    value={formData.specialRequests}
                    onChange={(e) =>
                      handleInputChange("specialRequests", e.target.value)
                    }
                    placeholder="Beritahu kami jika ada request khusus untuk acara Anda..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Add-ons */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F7F5F2] mb-2">
                    Tambahan
                  </h2>
                  <p className="text-[#F7F5F2]/70">
                    Tambah add-ons untuk melengkapi paket Anda (opsional)
                  </p>
                </div>

                {filteredAddOns.length === 0 ? (
                  <div className="py-12 text-center rounded-lg bg-white/5">
                    <Info className="w-12 h-12 text-[#D4AF7A]/50 mx-auto mb-3" />
                    <p className="text-[#F7F5F2]/70">
                      Tidak ada add-ons tersedia untuk paket yang dipilih
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredAddOns.map((addOn) => {
                      const isSelected = isAddOnSelected(addOn.id);
                      const quantity = getAddOnQuantity(addOn.id);

                      return (
                        <div
                          key={addOn.id}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            isSelected
                              ? "border-[#D4AF7A] bg-[#D4AF7A]/10"
                              : "border-[#D4AF7A]/20 bg-white/5"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-[#F7F5F2]">
                                {addOn.name}
                              </h3>
                              <p className="text-lg font-bold text-[#D4AF7A] mt-1">
                                {formatCurrency(addOn.price)}
                              </p>
                            </div>
                            <button
                              onClick={() => toggleAddOn(addOn)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                isSelected
                                  ? "bg-[#D4AF7A] text-black"
                                  : "bg-white/10 text-[#F7F5F2] hover:bg-white/20"
                              }`}
                            >
                              {isSelected ? "Hapus" : "Tambah"}
                            </button>
                          </div>

                          {isSelected && (
                            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#D4AF7A]/20">
                              <span className="text-sm text-[#F7F5F2]/70">
                                Jumlah:
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    updateAddOnQuantity(addOn.id, quantity - 1)
                                  }
                                  className="w-8 h-8 rounded bg-white/10 text-[#F7F5F2] hover:bg-white/20 flex items-center justify-center"
                                  disabled={quantity <= 1}
                                >
                                  <Minus size={14} />
                                </button>
                                <span className="w-8 text-center font-medium text-[#F7F5F2]">
                                  {quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateAddOnQuantity(addOn.id, quantity + 1)
                                  }
                                  className="w-8 h-8 rounded bg-white/10 text-[#F7F5F2] hover:bg-white/20 flex items-center justify-center"
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              <span className="text-sm font-medium text-[#D4AF7A] ml-auto">
                                {formatCurrency(addOn.price * quantity)}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Confirmation */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F7F5F2] mb-2">
                    Konfirmasi Booking
                  </h2>
                  <p className="text-[#F7F5F2]/70">
                    Review detail booking Anda sebelum submit
                  </p>
                </div>

                {/* Booking Summary */}
                <div className="space-y-4">
                  {/* Packages */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Paket yang Dipilih
                    </h3>
                    <div className="space-y-2">
                      {selectedPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-[#F7F5F2]/80">{pkg.name}</span>
                          <span className="font-medium text-[#D4AF7A]">
                            {formatCurrency(pkg.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add-ons */}
                  {selectedAddOns.length > 0 && (
                    <div className="p-4 rounded-lg bg-white/5">
                      <h3 className="font-semibold text-[#F7F5F2] mb-3">
                        Tambahan
                      </h3>
                      <div className="space-y-2">
                        {selectedAddOns.map((addOn) => (
                          <div
                            key={addOn.id}
                            className="flex items-center justify-between text-sm"
                          >
                            <span className="text-[#F7F5F2]/80">
                              {addOn.name} x {addOn.quantity}
                            </span>
                            <span className="font-medium text-[#D4AF7A]">
                              {formatCurrency(addOn.price * addOn.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Event Info */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Detail Acara
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <Calendar size={16} className="text-[#D4AF7A]" />
                        <span>
                          {new Date(formData.eventDate).toLocaleDateString(
                            "id-ID",
                            {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <Clock size={16} className="text-[#D4AF7A]" />
                        <span>{formData.eventTime}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <MapPin size={16} className="text-[#D4AF7A]" />
                        <span>{formData.eventLocation}</span>
                      </div>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Informasi Kontak
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <User size={16} className="text-[#D4AF7A]" />
                        <span>{formData.clientName}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <Mail size={16} className="text-[#D4AF7A]" />
                        <span>{formData.clientEmail}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                        <Phone size={16} className="text-[#D4AF7A]" />
                        <span>{formData.clientPhone}</span>
                      </div>
                      {formData.clientInstagram && (
                        <div className="flex items-center gap-2 text-[#F7F5F2]/80">
                          <Instagram size={16} className="text-[#D4AF7A]" />
                          <span>{formData.clientInstagram}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Summary */}
                  <div className="p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg">
                    <h3 className="font-semibold text-[#F7F5F2] mb-3">
                      Ringkasan Pembayaran
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-[#F7F5F2]/80">
                        <span>Subtotal</span>
                        <span>{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-[#F7F5F2]/80">
                        <span>Deposit ({totals.depositPercent}%)</span>
                        <span className="text-[#D4AF7A]">
                          {formatCurrency(totals.depositAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-[#F7F5F2]/80">
                        <span>Pelunasan di lokasi</span>
                        <span>{formatCurrency(totals.remainingAmount)}</span>
                      </div>
                      <div className="pt-2 mt-2 border-t border-[#D4AF7A]/30">
                        <div className="flex justify-between">
                          <span className="font-semibold text-[#F7F5F2]">
                            Total
                          </span>
                          <span className="text-xl font-bold text-[#D4AF7A]">
                            {formatCurrency(totals.subtotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info
                        size={16}
                        className="text-[#D4AF7A] mt-0.5 flex-shrink-0"
                      />
                      <p className="text-sm text-[#F7F5F2]/80">
                        Dengan melanjutkan, Anda setuju untuk membayar deposit
                        sebesar{" "}
                        <span className="font-semibold text-[#D4AF7A]">
                          {formatCurrency(totals.depositAmount)}
                        </span>{" "}
                        dalam 24 jam setelah booking dikonfirmasi. Kami akan
                        menghubungi Anda melalui WhatsApp untuk instruksi
                        pembayaran.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#D4AF7A]/20">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-6 py-3 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
                {currentStep === 1 ? "Kembali" : "Sebelumnya"}
              </button>

              {currentStep < STEPS.length ? (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
                >
                  {currentStep === STEPS.length - 1 ? "Konfirmasi" : "Lanjut"}
                  <ChevronRight size={18} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {submitting ? "Memproses..." : "Submit Booking"}
                </button>
              )}
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-[#F7F5F2]/60 text-sm">
              Butuh bantuan?{" "}
              <a
                href="https://wa.me/6289526775445"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF7A] hover:underline"
              >
                Hubungi kami via WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingForm;
